import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { getAppInfoDisplayString } from '@core/utils/core.util';
import { isStaleChunkError } from '@core/utils/stale-chunk.util';
import currentAppInfo from '@root/assets/app-info.json';
import { ConfirmService } from '@shared/components/confirm/confirm-dialog.component';
import { ConfirmCloseReason } from '@shared/shared.types';
import moment from 'moment';
import { firstValueFrom, Observable, of, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
} from 'rxjs/operators';
import { AppInfo } from '../core.types';
import { Logger } from './logger.service';

@Injectable({ providedIn: 'root' })
export class VersionService {
  private httpClient = inject(HttpClient);
  private confirmService = inject(ConfirmService);
  private router = inject(Router);

  currentAppInfo = currentAppInfo;
  checkVersion$: Observable<string>;

  private reloadPromptOpen = false;

  private readonly logger = new Logger(this.constructor.name);

  constructor() {
    this.checkVersion$ = timer(0, 300000).pipe(
      switchMap(() => this.checkVersion()),
      distinctUntilChanged(),
      shareReplay(1)
    );
    this.checkVersion$
      .pipe(filter((versionUpdate) => !!versionUpdate))
      .subscribe(() => {
        this.promptReload('core.version.update');
      });

    // A lazy route whose chunk fails to load rejects the navigation instead
    // of reaching ErrorHandler, so the router needs its own hook.
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationError => event instanceof NavigationError
        )
      )
      .subscribe((event) => this.handleStaleChunkError(event.error));
  }

  update() {
    this.logger.debug('update');
    window.location.reload();
  }

  handleStaleChunkError(error: unknown) {
    if (!isStaleChunkError(error)) {
      return;
    }
    this.promptReload('core.version.staleChunk');
  }

  private checkVersion(): Observable<string> {
    this.logger.debug('check version');
    return this.httpClient
      .get<AppInfo>(`/assets/app-info.json?timestamp=${moment().valueOf()}`)
      .pipe(
        catchError(() => of(null)),
        map((serverAppInfo: AppInfo) => {
          const serverVersionDisplayString =
            getAppInfoDisplayString(serverAppInfo);
          this.logger.debug(
            'current version',
            getAppInfoDisplayString(this.currentAppInfo),
            'server version',
            serverVersionDisplayString
          );
          if (
            serverAppInfo &&
            this.currentAppInfo &&
            (this.currentAppInfo.tag !== serverAppInfo.tag ||
              serverAppInfo.hash !== this.currentAppInfo.hash)
          ) {
            return serverVersionDisplayString;
          }
          return '';
        })
      );
  }

  /**
   * Asks the user whether to reload now. Only one prompt is ever on screen,
   * whichever trigger asked first.
   */
  private async promptReload(translationKey: string) {
    if (this.reloadPromptOpen) {
      return;
    }
    this.reloadPromptOpen = true;
    try {
      const result = await firstValueFrom(
        this.confirmService.confirm({
          title: `${translationKey}.title`,
          message: `${translationKey}.message`,
        }),
        { defaultValue: null }
      );
      if (result?.closeReason === ConfirmCloseReason.Yes) {
        this.update();
      }
    } finally {
      this.reloadPromptOpen = false;
    }
  }
}
